/**
 * Fundo personalizado da câmera: a imagem que a pessoa envia. Fica SÓ no navegador dela (IndexedDB), nunca vai para os
 * nossos servidores: é uma foto da casa ou do escritório. Aqui moram o seletor de arquivo, a validação, o preparo da
 * imagem e a guarda; o estado fica em `useCustomBackground`.
 *
 * Preparo: o processador do LiveKit estica a imagem sobre o quadro inteiro, então ela é cortada em 16:9 (centralizada,
 * sem esticar) e reduzida a 1280x720 em JPEG: uma foto de 12 MP do celular vira ~150 kB, que cabe na memória e carrega
 * depressa. Só o resultado é guardado.
 */

export const CUSTOM_BACKGROUND_ID = 'custom';

/** Maior arquivo aceito: foto de celular de boa resolução cabe folgada; acima disso quase certamente não é um fundo. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const BACKGROUND_SIZE = Object.freeze({ width: 1280, height: 720 });

export const ACCEPTED_TYPES = Object.freeze(['image/jpeg', 'image/png', 'image/webp']);

export const UPLOAD_ERRORS = Object.freeze({
    invalid: 'Escolha uma imagem JPG, PNG ou WebP de até 10 MB.',
    decode: 'Não foi possível ler essa imagem. Tente outra.',
    notSaved: 'Não foi possível guardar a imagem neste navegador: ela vale só nesta chamada.',
});

const DB_NAME = 'twc-video-call';
const STORE_NAME = 'backgrounds';
const KEY = 'custom';

/** O arquivo serve? `null` quando sim, senão a mensagem para a pessoa. */
export function validateBackgroundFile(file) {
    if (!file || !ACCEPTED_TYPES.includes(file.type) || file.size > MAX_UPLOAD_BYTES || file.size === 0) return UPLOAD_ERRORS.invalid;
    return null;
}

/**
 * Abre o seletor de arquivos NO DOCUMENTO DA SALA (`doc`: com a sala na janela flutuante não é o da guia) e devolve o
 * arquivo escolhido, ou `null` se a pessoa cancelou. O campo é criado na hora e some depois: o menu que o abriu pode fechar
 * antes de a pessoa escolher, e um campo dentro dele sumiria junto, sem avisar.
 * @param {Document} doc
 * @returns {Promise<File|null>}
 */
export function pickImageFile(doc) {
    return new Promise((resolve) => {
        const input = doc.createElement('input');
        input.type = 'file';
        input.accept = ACCEPTED_TYPES.join(',');
        input.style.display = 'none';
        const done = (file) => {
            input.remove();
            resolve(file);
        };
        input.addEventListener('change', () => done(input.files?.[0] ?? null));
        input.addEventListener('cancel', () => done(null));
        doc.body.appendChild(input);
        input.click();
    });
}

function defaultCanvas(width, height) {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

function canvasToJpeg(canvas) {
    if (typeof canvas.convertToBlob === 'function') return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 });
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob falhou'))), 'image/jpeg', 0.85);
    });
}

/**
 * Corta a imagem em 16:9 (preenchendo, centralizada) e a reduz a 1280x720 em JPEG.
 * `createBitmap` e `makeCanvas` só existem para teste. Lança um erro com a mensagem para a pessoa se não conseguir ler.
 * @param {Blob} file
 * @returns {Promise<Blob>}
 */
export async function prepareBackground(file, { createBitmap = (blob) => createImageBitmap(blob), makeCanvas = defaultCanvas } = {}) {
    let bitmap;
    try {
        bitmap = await createBitmap(file);
    } catch {
        throw new Error(UPLOAD_ERRORS.decode);
    }
    try {
        const { width, height } = BACKGROUND_SIZE;
        const canvas = makeCanvas(width, height);
        const context = canvas.getContext('2d');
        // Fundo branco: um PNG com transparência viraria preto ao ir para JPEG.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        const scale = Math.max(width / bitmap.width, height / bitmap.height);
        const drawWidth = bitmap.width * scale;
        const drawHeight = bitmap.height * scale;
        context.drawImage(bitmap, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
        return await canvasToJpeg(canvas);
    } catch {
        throw new Error(UPLOAD_ERRORS.decode);
    } finally {
        bitmap.close?.();
    }
}

const request = (req) => new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});

function openDatabase(idb) {
    return new Promise((resolve, reject) => {
        const open = idb.open(DB_NAME, 1);
        open.onupgradeneeded = () => open.result.createObjectStore(STORE_NAME);
        open.onsuccess = () => resolve(open.result);
        open.onerror = () => reject(open.error);
    });
}

/**
 * Onde a imagem preparada fica guardada. Sem IndexedDB (ou com ele recusando, como numa janela privativa de alguns
 * navegadores) a imagem vale só nesta sessão, na memória, e `save` avisa devolvendo `false`.
 * @param {{ indexedDB?: IDBFactory|null }} [options] `indexedDB`: para teste
 */
export function createBackgroundStore({ indexedDB: idb = typeof indexedDB === 'undefined' ? null : indexedDB } = {}) {
    let memory = null;

    async function withStore(mode, run) {
        const db = await openDatabase(idb);
        try {
            return await request(run(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME)));
        } finally {
            db.close();
        }
    }

    return {
        /** @returns {Promise<boolean>} guardou de forma durável? */
        async save(blob) {
            memory = blob;
            if (!idb) return false;
            try {
                await withStore('readwrite', (store) => store.put(blob, KEY));
                return true;
            } catch {
                return false;
            }
        },
        /** @returns {Promise<Blob|null>} */
        async load() {
            if (idb) {
                try {
                    const blob = await withStore('readonly', (store) => store.get(KEY));
                    if (blob) return blob;
                } catch {
                    // cai na memória
                }
            }
            return memory;
        },
        async remove() {
            memory = null;
            if (!idb) return;
            try {
                await withStore('readwrite', (store) => store.delete(KEY));
            } catch {
                // nada a apagar
            }
        },
    };
}

export const customBackgroundStore = createBackgroundStore();
